import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  Delete,
  Edit,
  GeneralCardComponent,
  LabelValue,
  PageTitleAndSubTitle,
  TextField,
  Tooltip,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useNavigate, useLocation } from 'react-router-dom';
import { COHORT_COMPARISON_INTERNAL } from '../../core-utils/routes';
import ComparisonTable from '../../_components/atoms/ComparisonTable';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { DueDiligenceDataType } from '../../core-utils/helper-utils/recommendedAppsHelper';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import Loader from '../../_components/molecules/Loader';
import { Pagination } from '@mui/material';
import NoDoc from '../../_components/atoms/Icons/NoDocuments';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { compilationDataProps } from '../TermCompilerPage';
import { useStores } from '../../stores';
import {
  COHORT_COMPARISON_TABLE_COLUMNS,
  CohortComparisonLocationProps,
  formatComparisonTableData,
  MENU_OPTIONS,
} from '../../core-utils/helper-utils/cohortComparisonHelper';
import usePlanDates from '../../core-utils/custom-hooks/PlanDates';
import { useAPIPayload } from '../../core-utils/helper-utils/apiPayloadHelper';
import TrendAnalysisPage from '../TrendAnalysisPage';
import BasicModal from '../../_components/atoms/BasicModal';
import ReportCreateNewLabelField from '../../_components/atoms/ReportCreateNewLabelField';
import { cohortComparisonMessages } from '../../core-utils/messages/cohortComparisonPageConstants';
import {
  ColorPickerCircle,
  trendSeriesData,
} from '../../core-utils/helper-utils/trendAnalysisHelper';
import CohortDetailsModal from '../../_components/organisms/CohortDetailsModalNew';
import useTotalEligible from '../../core-utils/custom-hooks/TotalEligible';
import TrendAnalysisConstants from '../../core-utils/messages/TrendAnalysisConstants';
import DateFilter from '../../_components/molecules/DateFilter';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import EditIcon from '@mui/icons-material/Edit';
// import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
// import { commonDropDownProps } from '../../_components/atoms/ReportsCohortItem';
export interface Document {
  document_name: string;
}
export interface ComparisonDataPros {
  _id: string;
  comparison_name?: string;
  vendor_type?: string;
  contract_ids?: string[];
  comparison_parameters?: any[];
  status:
    | 'Ready'
    | 'Failed Compilation'
    | 'Processing'
    | 'No Documents Uploaded';
  createdAt: string;
  documents: Document[];
}

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleAndSubtitle: {
    width: '1276px',
    padding: '32px 0px 32px 63px',
  },
  hr: {
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  body: {
    width: '1276px',
    padding: '40px 0px 32px 63px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  noDataGrid: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  tooltip: {
    backgroundColor: patientAccessTheme.palette.grey[900],
    padding: '10px',
    fontSize: '13px',
    fontFamily: 'Inter',
    fontWeight: 400,
    lineHeight: '16px',
    marginTop: '2px',
    color: patientAccessTheme.palette.status.white,
  },
  arrow: {
    color: patientAccessTheme.palette.grey[900],
    marginLeft: '50px',
  },
  popper: {
    marginTop: '-4px',
    marginLeft: '-34px',
  },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    height: '440px',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    marginTop: '4px',
  },
  badge: {
    fontSize: '11px',
    padding: '2px 8px',
    borderRadius: '100px',
    fontWeight: 500,
    marginRight: '6px',
  },
  metricSection: {
    marginTop: '8px',
  },
  chipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    marginTop: '4px',
  },
  metricLabel: {
    marginBottom: '6px',
  },
  chip: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '12px',
    background: patientAccessTheme.palette.grey[100],
    border: `0.5px solid ${patientAccessTheme.palette.grey[300]}`,
    borderRadius: '100px',
    padding: '2px 10px',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    flexShrink: 0,
  },
  flex: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
});

const CohortComparisonPage: React.FC<{}> = observer(({}) => {
  //Stores
  const employerGroupStore = useStores().employerGroupStore;
  const summaryCardsStore = useStores().summaryCardsStore;
  const quarterlyReportStore = useStores().quaterlyReportStore;
  const cohortDetailsStore = useStores().cohortDetailsStore;
  const loginPageStore = useStores().loginPageStore;

  // Constants
  const { classes } = useStyles();
  const location: CohortComparisonLocationProps = useLocation();
  const { TITLE, SUBTITLE, card } = RECOMMENDED_APPS.COHORT_COMPARISON;
  const navigate = useNavigate();
  const pageLimit = 10;

  const {
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    isIncurredDate,
    isPaidDate,
    isPlanDatesFetched,
    aggregationParameter,
    noDateFilter,
  } = quarterlyReportStore;

  const { getPlanDates } = usePlanDates();
  const payload = useAPIPayload();
  const abortController = new AbortController();

  const {
    overAllEnrolled,
    getTotalEligible: getTotalEligibleWithoutMemberIds,
  } = useTotalEligible(
    aggregationParameter === 'paid' ? paidStartDate : incurredStartDate,
    aggregationParameter === 'paid' ? paidEndDate : incurredEndDate,
    employerGroupStore,
  );

  // States
  const [comparisonData, setComparisonData] = useState<ComparisonDataPros[]>(
    [],
  );
  const [comparisonRawData, setComparisonRawData] = useState<[]>([]);
  const [mainLoading, setMainLoading] = useState(true);
  const [pageCount, setPageCount] = useState(10);
  const [tablePageNumber, setTablePageNumber] = useState(1);
  const [comparisonName, setComparisonName] = useState<string>('');
  const [cohortName, setCohortName] = useState<string>('');
  const [cohortColor, setCohortColor] = useState<string>('#3b82f6');
  const [trendName, setTrendName] = useState<string>('');
  // const [chartStyle, setChartStyle] = useState<string>('');
  const [openCohortDetailsModal, setOpenCohortDetailsModal] = useState<boolean>(
    false,
  );
  const [openNewComparisonModal, setOpenNewComparisonModal] = useState<boolean>(
    false,
  );
  const [trendAnalysisModalOpen, setTrendAnalysisModalOpen] = useState<boolean>(
    false,
  );
  const [editComparison, setEditComparison] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [cohortDetailsList, setCohortDetailsList] = useState<any[]>([]);
  const [trendList, setTrendList] = useState<any[]>([]);
  const [openAddAnotherModal, setOpenAddAnotherModal] = useState(false);
  const [openAddAnotherTrendModal, setOpenAddAnotherTrendModal] = useState(
    false,
  );
  const [openPreviewModal, setOpenPreviewModal] = useState(false);
  const [openTrendPreviewModal, setOpenTrendPreviewModal] = useState(false);
  const [editCohortIndex, setEditCohortIndex] = useState<number | null>(null);
  const [editTrendIndex, setEditTrendIndex] = useState<number | null>(null);
  const [editNameModal, setEditNameModal] = useState<{
    open: boolean;
    type: 'comparison' | 'cohort' | 'trend' | null;
    index: number | null;
    value: string;
    color?: string;
  }>({ open: false, type: null, index: null, value: '', color: undefined });
  const [editSourceComparisonId, setEditSourceComparisonId] = useState<
    string | null
  >(null);

  // Functions
  const handleCohortDetailsClose = () => {
    setOpenCohortDetailsModal(false);
  };

  const handlePagination = (_event: ChangeEvent<unknown>, page: number) => {
    setTablePageNumber(page);
  };

  const handleRowClick = (
    eg_nid: DueDiligenceDataType['eg_nid'],
    status: DueDiligenceDataType['status'] | compilationDataProps['status'],
    contract_ids: ComparisonDataPros['contract_ids'],
    comparison_name: ComparisonDataPros['comparison_name'],
    vendor_type: ComparisonDataPros['vendor_type'],
    comparison_id: ComparisonDataPros['_id'],
  ) => {
    const selectedComparison: any = comparisonRawData.find(
      (item: any) => item._id === comparison_id,
    );
    quarterlyReportStore.setAggregationParameter(
      selectedComparison?.aggregationParameter,
    );
    quarterlyReportStore.setIncurredStartDate(
      selectedComparison?.incurredStartDate,
    );
    quarterlyReportStore.setIncurredEndDate(
      selectedComparison?.incurredEndDate,
    );
    quarterlyReportStore.setPaidStartDate(selectedComparison?.paidStartDate);
    quarterlyReportStore.setPaidEndDate(selectedComparison?.paidEndDate);
    navigate(COHORT_COMPARISON_INTERNAL, {
      state: {
        comparison_id: selectedComparison?._id,
      },
    });
  };

  const handleMenuSelect = async (row: any, option: string) => {
    const selectedComparison: any = comparisonRawData.find(
      (item: any) => item._id === row._id,
    );
    switch (option) {
      case 'View Trend':
        navigate(COHORT_COMPARISON_INTERNAL, {
          state: {
            comparison_id: selectedComparison?._id,
          },
        });
        break;
      case 'Edit':
        handleEdit(selectedComparison);
        break;
      case 'Delete':
        await axios.post(
          properties.aiqBaseUrl +
            properties.deepDiveBaseUrl +
            properties.deleteCohortComparison,
          {
            comparison_id: row._id,
          },
        );
        getAllComparisons();
        break;
      default:
        break;
    }
  };

  const handleEdit = (selectedComparison: any) => {
    setCohortDetailsList(selectedComparison?.cohortDetails || []);
    setTrendList(selectedComparison?.trendList || []);
    setEditComparison(selectedComparison);
    setComparisonName(selectedComparison?.comparisonName || '');
    setIsEditMode(true);
    setOpenPreviewModal(true);
  };

  const handleCohortSave = () => {
    const currentCohort = cohortDetailsStore.cohortDetailsList;
    const currentToggleConditions = summaryCardsStore.toggleConditions;

    if (!currentCohort || currentCohort.length === 0) {
      return;
    }
    if (!cohortName) {
      return;
    }

    const updatedCohort = {
      cohortName,
      cohortColor,
      // chartStyle,
      cohortDetails: currentCohort,
      toggleConditions: currentToggleConditions,
    };

    if (isEditMode && editCohortIndex !== null) {
      // ✅ UPDATE existing cohort
      setCohortDetailsList((prev) =>
        prev.map((item, index) =>
          index === editCohortIndex ? updatedCohort : item,
        ),
      );
    } else {
      // ✅ ADD new cohort
      setCohortDetailsList((prev) => [...prev, updatedCohort]);
    }

    // Clear store
    cohortDetailsStore.removeCohortDetailsList();
    summaryCardsStore.removeToggleConditions();

    setCohortName('');
    setCohortColor('#3b82f6');
    // setChartStyle('');
    setEditCohortIndex(null);
    setOpenCohortDetailsModal(false);
    setOpenAddAnotherModal(true);
  };

  const handleDeleteCohort = (indexToDelete: number) => {
    setCohortDetailsList((prev) =>
      prev.filter((_, index) => index !== indexToDelete),
    );
  };

  const handleEditCohort = (indexToEdit: number) => {
    const cohortToEdit = cohortDetailsList[indexToEdit];

    cohortDetailsStore.setCohortDetailsList(cohortToEdit.cohortDetails);
    summaryCardsStore.addToggleConditions(cohortToEdit.toggleConditions || []);
    setCohortColor(cohortToEdit.cohortColor || '#3b82f6');
    setCohortName(cohortToEdit.cohortName);
    // setChartStyle(cohortToEdit.chartStyle);
    setEditCohortIndex(indexToEdit);
    setIsEditMode(true);
    setOpenCohortDetailsModal(true);
  };

  const handleTrendSave = (
    timeGrain: string,
    primaryAxisData: trendSeriesData,
  ) => {
    if (!trendName) {
      return;
    }
    const updatedTrend = {
      trendName,
      timeGrain,
      primaryAxisData,
    };
    if (editTrendIndex !== null) {
      // ✅ UPDATE existing trend
      setTrendList((prev) =>
        prev.map((item, index) =>
          index === editTrendIndex ? updatedTrend : item,
        ),
      );
      setEditTrendIndex(null);
    } else {
      // ✅ ADD new trend
      setTrendList((prev) => [...prev, updatedTrend]);
    }

    setTrendName('');
    setTrendAnalysisModalOpen(false);
    setOpenAddAnotherTrendModal(true);
  };

  const handleDeleteTrend = (index: number) => {
    setTrendList((prev) => prev.filter((_, i) => i !== index));
  };

  const handleEditTrend = (index: number) => {
    setEditTrendIndex(index);
    setTrendName(trendList[index].trendName);
    setTrendAnalysisModalOpen(true);
  };

  const handleEditNameSave = () => {
    switch (editNameModal.type) {
      case 'comparison':
        setComparisonName(editNameModal.value);
        break;
      case 'cohort':
        if (editNameModal.index !== null) {
          setCohortDetailsList((prev) =>
            prev.map((item, i) =>
              i === editNameModal.index
                ? {
                    ...item,
                    cohortName: editNameModal.value,
                    cohortColor: editNameModal.color ?? item.cohortColor, // ← add this
                  }
                : item,
            ),
          );
        }
        break;
      case 'trend':
        if (editNameModal.index !== null) {
          setTrendList((prev) =>
            prev.map((item, i) =>
              i === editNameModal.index
                ? { ...item, trendName: editNameModal.value }
                : item,
            ),
          );
        }
        break;
    }
    setEditNameModal({ open: false, type: null, index: null, value: '' });
  };

  const handleEditCancel = () => {
    if (editSourceComparisonId) {
      navigate(COHORT_COMPARISON_INTERNAL, {
        state: { comparison_id: editSourceComparisonId },
      });
      setEditSourceComparisonId(null);
    }
    // reset edit state
    setIsEditMode(false);
    setEditComparison(null);
    setCohortDetailsList([]);
    setTrendList([]);
    setComparisonName('');
    setOpenPreviewModal(false);
    setOpenTrendPreviewModal(false);
  };

  // Apis
  const handleSubmit = async () => {
    const newComparison = await axios.post(
      properties.aiqBaseUrl +
        properties.deepDiveBaseUrl +
        properties.createOrUpdateCohortComparison,
      {
        ...(editComparison && { comparison_id: editComparison?._id }),
        comparisonName,
        ...payload,
        cohortDetails: cohortDetailsList,
        trendList,
        createdBy: `${loginPageStore.firstName} ${loginPageStore.lastName}`,
        updatedBy: `${loginPageStore.firstName} ${loginPageStore.lastName}`,
      },
    );
    // Clear store
    cohortDetailsStore.removeCohortDetailsList();
    summaryCardsStore.removeToggleConditions();
    setTrendAnalysisModalOpen(false);
    setIsEditMode(false);
    setEditComparison(null);
    setEditSourceComparisonId(null);
    navigate(COHORT_COMPARISON_INTERNAL, {
      state: {
        comparison_id: newComparison.data?.data?._id,
      },
    });
  };

  const getAllComparisons = async () => {
    setMainLoading(true);
    try {
      const res = await axios.post(
        properties.aiqBaseUrl +
          properties.deepDiveBaseUrl +
          properties.getCohortComparisons,
        {
          ...payload,
          page: tablePageNumber,
          limit: pageLimit,
        },
      );
      setComparisonRawData(res?.data?.data || []);
      const formattedData = formatComparisonTableData(res?.data?.data || []);
      setComparisonData(formattedData);
      setPageCount(Math.ceil(res.data.totalCount / pageLimit));
    } catch (error) {
      console.error('Error fetching cohort comparisons:', error);
    } finally {
      setMainLoading(false);
    }
  };

  // UseEffects
  useEffect(() => {
    getAllComparisons();
    return () => {
      abortController.abort();
    };
  }, [
    tablePageNumber,
    isPlanDatesFetched,
    isIncurredDate,
    isPaidDate,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  ]);

  useEffect(() => {
    getTotalEligibleWithoutMemberIds();
    return () => {
      abortController.abort();
    };
  }, [
    isPlanDatesFetched,
    isIncurredDate,
    isPaidDate,
    incurredStartDate,
    incurredEndDate,
    paidStartDate,
    paidEndDate,
    aggregationParameter,
  ]);

  useEffect(() => {
    const fetchPlanDates = async () => {
      if (!isPlanDatesFetched) {
        await getPlanDates();
      }
    };
    if (!employerGroupStore?.employerGroupResponseData?.eg_nid[0]) {
      return;
    }
    fetchPlanDates();
    // Clear store
    cohortDetailsStore.removeCohortDetailsList();
    summaryCardsStore.removeToggleConditions();
  }, [employerGroupStore?.employerGroupResponseData?.eg_nid]);

  useEffect(() => {
    if (location.state?.triggerEdit) {
      setEditSourceComparisonId(location.state.triggerEdit._id); // ← capture id
      handleEdit(location.state.triggerEdit);
      navigate(`${window.location.pathname}${window.location.search}`, {
        replace: true,
        state: {},
      });
    }
  }, [location.state?.triggerEdit]);

  return (
    <Grid container className={classes.root}>
      <Grid className={classes.titleAndSubtitle}>
        <PageTitleAndSubTitle
          title={TITLE}
          subTitle={SUBTITLE}
          customButton={true}
          customButtonText={cohortComparisonMessages.NEW}
          customButtonClick={() => {
            setOpenNewComparisonModal(true);
          }}
          customButtonWidth="150px"
          customButtonVariant={'contained'}
        />
      </Grid>
      <div className={classes.hr} />
      <Grid className={classes.body}>
        <CardComponent
          title={
            <Typography
              fontSize="14px"
              fontType="semiBold"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {card.title}
            </Typography>
          }
          subTitle={card.subTitle}
          body={
            <Grid
              style={{
                marginTop: '10px',
              }}
            >
              {mainLoading ? (
                <Loader height="300px" />
              ) : comparisonData?.length > 0 ? (
                <Grid>
                  <ComparisonTable
                    columns={COHORT_COMPARISON_TABLE_COLUMNS}
                    rowsData={comparisonData}
                    handleRowClick={handleRowClick}
                    menuOptions={MENU_OPTIONS}
                    showMenu={true}
                    onMenuSelect={handleMenuSelect}
                  />
                  <Box className={classes.pagination}>
                    <Pagination
                      count={pageCount}
                      page={tablePageNumber}
                      boundaryCount={1}
                      color="primary"
                      size="small"
                      shape="circular"
                      onChange={handlePagination}
                    />
                  </Box>
                </Grid>
              ) : (
                <Grid className={classes.noDataGrid}>
                  <NoDoc />
                  <Typography
                    fontSize="16px"
                    fontType="semiBold"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                    margin="16px 0px 10px 0px"
                  >
                    {card.noData.title}
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.mediumemphasis}
                  >
                    {card.noData.subTitle}
                  </Typography>
                </Grid>
              )}
            </Grid>
          }
        />
      </Grid>
      <BasicModal
        open={openNewComparisonModal}
        handleClose={() => setOpenNewComparisonModal(false)}
        title={cohortComparisonMessages.TITLE}
        isSaveButton={true}
        saveBtnText={cohortComparisonMessages.SAVE}
        saveBtnAction={() => {
          setOpenNewComparisonModal(false);
          setOpenCohortDetailsModal(true);
        }}
        saveBtnDisabled={!comparisonName || !cohortName}
        isCloseBtn={true}
        height="auto"
        width="auto"
      >
        <LabelValue
          label={TrendAnalysisConstants.SELECTED_RANGE}
          value={
            <Grid style={{ display: 'flex', gap: '5px', alignItems: 'center' }}>
              <DateFilter showNoDateFilterOption={true} />
              {noDateFilter && (
                <Tooltip
                  arrow
                  classes={{
                    tooltip: classes.tooltip,
                    arrow: classes.arrow,
                    popper: classes.popper,
                  }}
                  title={TrendAnalysisConstants.NO_DATE_FILTER}
                  placement="bottom-start"
                >
                  <InfoOutlinedIcon
                    style={{
                      height: '16px',
                      width: '16px',
                      marginLeft: '0.5rem',
                      fill: patientAccessTheme.palette.status.icon,
                    }}
                  />
                </Tooltip>
              )}
            </Grid>
          }
          labelsMinWidth="150px"
        />
        <ReportCreateNewLabelField
          label={cohortComparisonMessages.NAME}
          width="100%"
          component={
            <TextField
              value={comparisonName}
              placeholder={cohortComparisonMessages.NAME_PLACEHOLDER}
              height="36px"
              width="100%"
              fullWidth
              onChange={(e: any) => {
                setComparisonName(e.target.value);
              }}
            />
          }
          marginBottom="12px"
        />
        <ReportCreateNewLabelField
          label={cohortComparisonMessages.COHORT}
          width="100%"
          component={
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ColorPickerCircle
                color={cohortColor}
                onChange={(newColor) => setCohortColor(newColor)}
              />
              <TextField
                value={cohortName}
                placeholder={cohortComparisonMessages.COHORT_PLACEHOLDER}
                height="36px"
                width="100%"
                fullWidth
                onChange={(e: any) => setCohortName(e.target.value)}
              />
            </Grid>
          }
          marginBottom="12px"
        />
        {/* <ReportCreateNewLabelField
          label={cohortComparisonMessages.CHART_STYLE}
          component={
            <Dropdown
              label={TrendAnalysisConstants.CHART_STYLE}
              dropDownOptions={cohortComparisonMessages.STYLE_OPTIONS}
              dropDownValues={cohortComparisonMessages.STYLE_OPTIONS}
              value={chartStyle}
              setValue={(value: string) => setChartStyle(value)}
              selectStyles={{
                ...commonDropDownProps.selectStyles,
                width: '290px',
              }}
              labelTop="10px"
              onChange={(event: any) => {
                setChartStyle(event.target.value);
              }}
              error={!chartStyle}
            />
          }
        /> */}
      </BasicModal>
      <BasicModal
        open={openAddAnotherModal}
        title={cohortComparisonMessages.ANOTHER}
        isSaveButton={true}
        saveBtnText={cohortComparisonMessages.ADD}
        saveBtnAction={() => {
          setOpenAddAnotherModal(false);
          setOpenCohortDetailsModal(true);
        }}
        saveBtnDisabled={!cohortName}
        isCloseBtn={true}
        closeBtnText={cohortComparisonMessages.CANCEL}
        handleClose={() => {
          setOpenAddAnotherModal(false);
          setOpenPreviewModal(true);
        }}
        height="auto"
        width="auto"
      >
        <Typography>{cohortComparisonMessages.CREATE_NEW}</Typography>
        <ReportCreateNewLabelField
          label=""
          width="100%"
          component={
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ColorPickerCircle
                color={cohortColor}
                onChange={(newColor) => setCohortColor(newColor)}
              />
              <TextField
                value={cohortName}
                placeholder={cohortComparisonMessages.COHORT_PLACEHOLDER}
                height="36px"
                width="100%"
                fullWidth
                onChange={(e: any) => setCohortName(e.target.value)}
              />
            </Grid>
          }
          marginBottom="12px"
        />
        {/* <ReportCreateNewLabelField
          label={cohortComparisonMessages.CHART_STYLE}
          component={
            <Dropdown
              label={TrendAnalysisConstants.CHART_STYLE}
              dropDownOptions={cohortComparisonMessages.STYLE_OPTIONS}
              dropDownValues={cohortComparisonMessages.STYLE_OPTIONS}
              value={chartStyle}
              setValue={(value: string) => setChartStyle(value)}
              selectStyles={{
                ...commonDropDownProps.selectStyles,
                width: '290px',
              }}
              labelTop="10px"
              onChange={(event: any) => {
                setChartStyle(event.target.value);
              }}
              error={!chartStyle}
            />
          }
        /> */}
      </BasicModal>
      <CohortDetailsModal
        open={openCohortDetailsModal}
        handleClose={handleCohortDetailsClose}
        totalResults={+overAllEnrolled.totalCount}
        isEditMode={isEditMode}
        onSave={handleCohortSave}
      />
      <BasicModal
        open={openPreviewModal}
        handleClose={
          isEditMode ? handleEditCancel : () => setOpenPreviewModal(false)
        }
        title={
          editComparison
            ? cohortComparisonMessages.EDIT
            : cohortComparisonMessages.PREVIEW
        }
        isSaveButton={true}
        saveBtnText={cohortComparisonMessages.PROCEED}
        saveBtnAction={() => {
          setOpenPreviewModal(false);
          if (isEditMode && trendList.length > 0) {
            setOpenTrendPreviewModal(true);
          } else {
            setOpenAddAnotherTrendModal(true);
          }
        }}
        isCloseBtn={false}
        width="600px"
        height="600px"
        isScrollable={true}
      >
        <Grid className={classes.preview}>
          {/* Comparison Name */}
          <Grid className={classes.flex}>
            <Typography
              fontSize="16px"
              fontType="semiBold"
              lineHeight="24px"
              textColor={patientAccessTheme.palette.text.highemphasis}
              margin="0px 8px 0px 0px"
            >
              {comparisonName}
            </Typography>
            <EditIcon
              style={{ cursor: 'pointer' }}
              onClick={() =>
                setEditNameModal({
                  open: true,
                  type: 'comparison',
                  index: null,
                  value: comparisonName,
                })
              }
            />
          </Grid>

          {/* Cohorts */}
          {cohortDetailsList.map((cohort: any, index: number) => (
            <GeneralCardComponent
              key={index}
              title={
                <Grid className={classes.flex}>
                  <span
                    className={classes.dot}
                    style={{ background: cohort.cohortColor || '#3b82f6' }}
                  />
                  <span>{`Cohort ${index + 1}: ${cohort.cohortName}`}</span>
                  <EditIcon
                    style={{ cursor: 'pointer', fontSize: '16px' }}
                    onClick={() =>
                      setEditNameModal({
                        open: true,
                        type: 'cohort',
                        index,
                        value: cohort.cohortName,
                        color: cohort.cohortColor || '#3b82f6', // ← add this
                      })
                    }
                  />
                </Grid>
              }
              // title={`Cohort ${index + 1}: ${
              //   cohort.cohortName
              // } | Chart Style: ${cohort?.chartStyle}`}
              titleRightMostItem={
                <>
                  <Edit
                    onClick={() => handleEditCohort(index)}
                    style={{ cursor: 'pointer' }}
                  />
                  <Delete
                    onClick={() => handleDeleteCohort(index)}
                    style={{ cursor: 'pointer' }}
                  />
                </>
              }
              body={
                <Grid>
                  <ul style={{ marginTop: '8px', paddingLeft: '16px' }}>
                    {cohort.cohortDetails.map((item: any, idx: number) => (
                      <li key={idx}>
                        <Typography
                          fontSize="14px"
                          fontType="regular"
                          lineHeight="21px"
                          textColor={
                            patientAccessTheme.palette.text.darkeremphasis
                          }
                        >
                          {item.category} {item.condition}{' '}
                          {item.property || item.multipleProperties?.join(', ')}
                        </Typography>
                      </li>
                    ))}
                  </ul>
                </Grid>
              }
              mainGridStyles={{ minHeight: 'fit-content' }}
            />
          ))}
        </Grid>
      </BasicModal>
      <BasicModal
        open={openAddAnotherTrendModal}
        title={cohortComparisonMessages.NEW_TREND}
        isSaveButton={true}
        saveBtnText={cohortComparisonMessages.NEW_TREND}
        saveBtnAction={() => {
          setOpenAddAnotherTrendModal(false);
          setTrendAnalysisModalOpen(true);
        }}
        saveBtnDisabled={!trendName}
        isCloseBtn={trendList?.length > 0 ? true : false}
        closeBtnText={cohortComparisonMessages.CANCEL}
        handleClose={() => {
          setOpenAddAnotherTrendModal(false);
          setOpenTrendPreviewModal(true);
        }}
        height="auto"
        width="auto"
      >
        <Typography>
          {trendList?.length === 0
            ? cohortComparisonMessages.TREND_NAME
            : cohortComparisonMessages.ANOTHER_TREND}
        </Typography>
        <ReportCreateNewLabelField
          label=""
          width="100%"
          component={
            <TextField
              value={trendName}
              placeholder={cohortComparisonMessages.TREND_PLACEHOLDER}
              height="36px"
              width="100%"
              fullWidth
              onChange={(e: any) => {
                setTrendName(e.target.value);
              }}
            />
          }
          marginBottom="12px"
        />
      </BasicModal>
      <BasicModal
        open={openTrendPreviewModal}
        handleClose={
          isEditMode ? handleEditCancel : () => setOpenTrendPreviewModal(false)
        }
        title={cohortComparisonMessages.TREND_PREVIEW}
        isSaveButton={true}
        saveBtnText={
          isEditMode // ← changed
            ? cohortComparisonMessages.UPDATE_ANALYSIS
            : cohortComparisonMessages.CREATE_ANALYSIS
        }
        saveBtnAction={handleSubmit}
        isCloseBtn={false}
        width="600px"
        height="600px"
        isScrollable={true}
      >
        <Grid className={classes.preview}>
          {trendList.map((trend, index) => {
            const { trendName, timeGrain, primaryAxisData } = trend;
            const { unit, metrics } = primaryAxisData;

            return (
              <GeneralCardComponent
                key={index}
                title={
                  <Grid className={classes.flex}>
                    <span>{`Trend ${index + 1}: ${trendName}`}</span>
                    <EditIcon
                      style={{ cursor: 'pointer', fontSize: '16px' }}
                      onClick={() =>
                        setEditNameModal({
                          open: true,
                          type: 'trend',
                          index,
                          value: trendName,
                        })
                      }
                    />
                  </Grid>
                }
                titleRightMostItem={
                  <>
                    <Edit
                      onClick={() => handleEditTrend(index)}
                      style={{ cursor: 'pointer' }}
                    />
                    <Delete
                      onClick={() => handleDeleteTrend(index)}
                      style={{ cursor: 'pointer' }}
                    />
                  </>
                }
                body={
                  <Grid>
                    {/* Metadata badges */}
                    <Grid className={classes.badgeRow}>
                      {[
                        {
                          label: `Time Grain: ${timeGrain}`,
                          bg: '#E6F1FB',
                          color: '#185FA5',
                        },
                        {
                          label: `Unit: ${unit}`,
                          bg: '#F1EFE8',
                          color: '#5F5E5A',
                        },
                      ].map(({ label, bg, color }) => (
                        <span
                          key={label}
                          className={classes.badge}
                          style={{ background: bg, color }}
                        >
                          {label}
                        </span>
                      ))}
                    </Grid>

                    {/* Metrics */}
                    {metrics.map((metric: any, mIdx: number) => (
                      <Grid key={mIdx} className={classes.metricSection}>
                        <Typography
                          fontSize="12px"
                          fontType="semiBold"
                          textColor={
                            patientAccessTheme.palette.text.mediumemphasis
                          }
                          margin="0 0 4px"
                        >
                          {metric.name}
                        </Typography>
                        <Grid className={classes.chipRow}>
                          {
                             
                            metric.options.map((opt: any, oIdx: number) => (
                              <span key={oIdx} className={classes.chip}>
                                <Typography
                                  fontSize="12px"
                                  fontType="regular"
                                  textColor={
                                    patientAccessTheme.palette.text.highemphasis
                                  }
                                >
                                  {opt.name}
                                </Typography>
                              </span>
                            ))
                          }
                        </Grid>
                      </Grid>
                    ))}
                  </Grid>
                }
                mainGridStyles={{ minHeight: 'fit-content' }}
              />
            );
          })}
        </Grid>
      </BasicModal>
      <BasicModal
        open={editNameModal.open}
        handleClose={() =>
          setEditNameModal({ open: false, type: null, index: null, value: '' })
        }
        title={
          editNameModal.type === 'comparison'
            ? 'Edit Comparison Name'
            : editNameModal.type === 'cohort'
            ? 'Edit Cohort Name'
            : 'Edit Trend Name'
        }
        isSaveButton={true}
        saveBtnText="Save"
        saveBtnAction={handleEditNameSave}
        saveBtnDisabled={!editNameModal.value.trim()}
        isCloseBtn={true}
        height="auto"
        width="auto"
      >
        <ReportCreateNewLabelField
          label={
            editNameModal.type === 'cohort'
              ? cohortComparisonMessages.COHORT
              : 'Name'
          }
          width="100%"
          component={
            <Grid style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {editNameModal.type === 'cohort' && (
                <ColorPickerCircle
                  color={editNameModal.color || '#3b82f6'}
                  onChange={(newColor) =>
                    setEditNameModal((prev) => ({ ...prev, color: newColor }))
                  }
                />
              )}
              <TextField
                value={editNameModal.value}
                placeholder="Enter name"
                height="36px"
                width="100%"
                fullWidth
                onChange={(e: any) =>
                  setEditNameModal((prev) => ({
                    ...prev,
                    value: e.target.value,
                  }))
                }
              />
            </Grid>
          }
          marginBottom="12px"
        />
      </BasicModal>
      {trendAnalysisModalOpen && (
        <TrendAnalysisPage
          isModal={true}
          open={trendAnalysisModalOpen}
          handleClose={() => {
            setTrendAnalysisModalOpen(false);
            if (editTrendIndex !== null) {
              setOpenTrendPreviewModal(true);
              setEditTrendIndex(null);
            }
          }}
          isCohortComparison={true}
          handleModalSubmit={handleTrendSave}
          modalPrimaryAxisData={
            editTrendIndex !== null
              ? JSON.parse(
                  JSON.stringify(trendList[editTrendIndex].primaryAxisData),
                ) // ✅ prefill from existing trend
              : JSON.parse(
                  JSON.stringify(
                    cohortComparisonMessages.DEFAULT_PRIMARY_AXIS_DATA,
                  ),
                )
          }
          modalTimeGrain={
            editTrendIndex !== null
              ? trendList[editTrendIndex].timeGrain
              : 'Monthly'
          }
          isEdit={editTrendIndex !== null || !!editComparison}
        />
      )}
    </Grid>
  );
});

export default CohortComparisonPage;
